import MembershipsMixin from '@/mixins/MembershipsMixin'
import getClosestOrgLikeTeam from '@/utils/getClosestOrgLikeTeam'
import getClosestOrgLikeTeamId from '@/utils/getClosestOrgLikeTeamId'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'

export default {
  mixins: [MembershipsMixin],
  methods: {
    ...mapActions(['archiveTeam', 'deleteTeam']),
    isRootTeam(team) {
      return team ? !team.parentId : false
    },
    isRootTeamId(teamId) {
      const team = this.$store.getters.teamById(teamId)
      return this.isRootTeam(team)
    },
    async performTeamArchival({ action, teamId, orgId }) {
      try {
        await this.archiveTeam({ orgId, teamId, action })
        let message = 'Team has been archived'
        if (action === 'unarchive') {
          message = 'Team has been restored'
        }
        EventBus.$emit('ADD_TOAST', { message })
        return true
      } catch (e) {
        EventBus.$emit('ADD_TOAST', {
          message: R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e),
          type: TOAST_TYPE.ERROR
        })
      }
    },
    async performTeamDeletion({ teamId, orgId }) {
      try {
        await this.deleteTeam({ orgId, teamId })
        EventBus.$emit('ADD_TOAST', { message: 'Team has been deleted' })
        return true
      } catch (e) {
        EventBus.$emit('ADD_TOAST', {
          message: R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e),
          type: TOAST_TYPE.ERROR
        })
      }
    },
    /**
     * This returns an array of root teams. Each team contains immediate children in key - children
     * @param childrenType - This says what type of children to include. possible values - all, orgLikeOnly
     * @param teams - teams for which the tree is to be created
     * @param includeArchived - true if archived teams should be included, false by default
     * @returns {*}
     */
    getTeamTree(childrenType = 'all', teams = null, includeArchived = false) {
      if (!teams) {
        teams = R.clone(Object.values(this.$store.state.teams))
        if (!includeArchived) {
          teams = teams.filter(x => !x.isArchived)
        }
      }
      teams.forEach(team => {
        team.children = team.children || []
      })
      const insertInOrder = (arr, item) => {
        const pos = arr.findIndex(({ id }) => item.id < id)
        return R.insert(pos, item, arr)
      }
      teams.forEach(team => {
        if (team.parentId) {
          const parentTeam = teams.find(parent => parent.id === team.parentId)
          if (!parentTeam) {
            console.warn('Could not find parent team for', team)
            return
          }
          if (!parentTeam.children) {
            parentTeam.children = []
          }
          if (childrenType !== 'orgLikeOnly' || team.isOrgLike) {
            parentTeam.children = insertInOrder(parentTeam.children, team)
          }
        }
      })
      // root teams are sorted based on organizationId, not teamId because teamId(for org) got assigned randomly. To retain the order in which orgs are shown, we are having an exception to have root teams sorted on organizatonId, not teamId.
      return teams
        .filter(team => this.isRootTeam(team))
        .sort((a, b) => a.organizationId - b.organizationId)
    },
    searchInTeamTree(teams, teamId) {
      for (let team of teams) {
        if (team.id === teamId) return team
        else if (team.children?.length > 0) {
          const teamFound = this.searchInTeamTree(team.children, teamId)
          if (teamFound) return teamFound
        }
      }
    },
    flattenTeamTree(teamTree) {
      return teamTree.reduce((acc, team) => {
        if (team.children?.length > 0) {
          acc = [...acc, team]
          const children = this.flattenTeamTree(team.children)
          acc = [...acc, ...children]
        } else {
          acc.push(team)
        }
        return acc
      }, [])
    },
    /**
     * This returns parentId of team.
     * For root teams, it returns id of the root team itself - We're doing this because we always want to have some subOrgId in the url
     * @param teamId
     * @returns {*}
     */
    getClosestOrgLikeTeamId(teamId) {
      return getClosestOrgLikeTeamId(this.$store, teamId)
    },
    getClosestOrgLikeTeam(teamId) {
      return getClosestOrgLikeTeam(this.$store, teamId)
    },
    getOrgLikeTeamsOfOrg(orgId) {
      return Object.values(this.$store.state.teams).filter(
        team => team.organizationId === orgId && team.isOrgLike
      )
    }
  }
}
